/**
 * @file DateInput.js
 * @version 2.4.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2025-11-12
 * @modified 2026-10-14
 * @description Custom date input component using react-datepicker with theme integration
 *
 * 2.3.0 (2026-10-08): `shortYear` shows and takes DD/MM/YY (a typed
 * four-digit year still parses) — where the screen reads dates DD/MM/YY.
 * 2.4.0 (2026-10-14): `max` (YYYY-MM-DD) stops the calendar at that day, as
 * `min` does — e.g. a formulation's date, which cannot be in the future.
 */

import React from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateInput.css';

// Shown as the first, typed as either: "08/10/26" and "08/10/2026" both
// parse (date-fns reads a two-digit year into this century or the last).
const SHORT_YEAR_FORMATS = ['dd/MM/yy', 'dd/MM/yyyy'];

// `min` (YYYY-MM-DD) stops the calendar at that day — e.g. the Xero lock.
// `max` (YYYY-MM-DD) stops it at the other end — e.g. no future dates.
// `shortYear` shows DD/MM/YY instead of DD/MM/YYYY.
function DateInput({ value, onChange, label, disabled, min, max, shortYear = false, ...props }) {
  // Convert string date (YYYY-MM-DD) to Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    // Handle YYYY-MM-DD format
    const date = new Date(dateString + 'T00:00:00');
    return isNaN(date.getTime()) ? null : date;
  };

  // Convert Date object to string (YYYY-MM-DD)
  const formatDate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const selectedDate = parseDate(value);

  const handleDateChange = (date) => {
    // Maintain backward compatibility by creating a synthetic event
    const formattedDate = formatDate(date);
    const syntheticEvent = {
      target: {
        value: formattedDate,
      },
    };
    onChange(syntheticEvent);
  };

  return (
    <div className="date-input-wrapper">
      {label && <label className="date-input-label">{label}</label>}
      <DatePicker
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={shortYear ? SHORT_YEAR_FORMATS : 'dd/MM/yyyy'}
        className="date-input"
        wrapperClassName="date-input-picker-wrapper"
        disabled={disabled}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        placeholderText="Pick date..."
        popperPlacement="bottom-start"
        portalId="datepicker-portal"
        minDate={min ? parseDate(min) : undefined}
        maxDate={max ? parseDate(max) : undefined}
        {...props}
      />
    </div>
  );
}

export default DateInput;
